// Publish switch for the legal notice pages (see siteLegal.ts). siteLegal has no imports, so no cycle.
import { LEGAL_TEXTS_READY } from '../components/legal-page/siteLegal';
/**
 * Localized routes configuration.
 *
 * Single source of truth for every URL path on the site, per language.
 * - Routes (App.tsx) are generated from this map: one Route per
 *   (RouteName, Language) pair.
 * - Components reference routes by name via pathFor() instead of hard-coded
 *   string templates, so renaming a slug only touches this file.
 * - Old English-only paths are kept as legacy redirects in App.tsx and as
 *   server-side 301s in netlify.toml.
 *
 * Finnish slugs use ä/ö literally — confirmed safe on modern browsers and
 * Netlify, and they match Finnish search terms exactly.
 *
 * Polish (pl) is a single-page landing in this app; pl paths exist for
 * parity but every /pl/<inner-page> URL gets redirected to /pl by
 * PageLayout, so the pl slug list mirrors English.
 */

export type Lang = 'en' | 'fi' | 'sv' | 'da' | 'de' | 'pl';

export const SUPPORTED_LANGS: readonly Lang[] = ['en', 'fi', 'sv', 'da', 'de', 'pl'];

export type RouteName =
  | 'productsOverview'
  | 'smokeDetectors'
  | 'centralUnit'
  | 'thermostats'
  | 'underfloorHeating'
  | 'radiatorHeating'
  | 'conditionSensors'
  | 'climaPriDisplay'
  | 'apartmentSensor'
  | 'outdoorSensor'
  | 'waterMeters'
  | 'waterMetersUltrimisNeo'
  | 'waterMetersUltrasonicWireless'
  | 'waterMetersUltrasonicMbus'
  | 'waterMetersMechanical'
  | 'energyMeters'
  | 'sensoStarU'
  | 'sensoStarC'
  | 'flowStarU'
  | 'myMetec'
  | 'partnerPropertyManager'
  | 'partnerDesigner'
  | 'partnerContractor'
  | 'designMaterials'
  | 'blog'
  | 'smartBuildingGuide'
  | 'energyEfficiencyDirective'
  | 'avaraWaterCase'
  | 'ikaalinenHeatingCase'
  | 'swedenExpansion'
  | 'fiMandatoryWaterMeter'
  | 'fiMeteringSavings'
  | 'fiMeterTechComparison'
  | 'fiLeakDetection'
  | 'fiDistrictHeatSavings'
  | 'fiSmokeAlarmDuty'
  | 'fiWaterBilling'
  | 'fiEnergyGrant'
  | 'housingCompanies'
  | 'savingsEstimate'
  | 'solutionsOverview'
  | 'solutionsEnergyControl'
  | 'solutionsRoomComfort'
  | 'solutionsMetering'
  | 'aboutUs'
  | 'careers'
  | 'contact'
  | 'privacyPolicy'
  | 'termsOfService'
  | 'cookiePolicy'
  | 'legalNotice';

type LangPaths = Record<Lang, string>;

export const ROUTES: Record<RouteName, LangPaths> = {
  // ─── Products ────────────────────────────────────────────────────────
  productsOverview: {
    en: '/products',
    fi: '/tuotteet',
    sv: '/produkter',
    da: '/produkter',
    de: '/produkte',
    pl: '/products',
  },
  smokeDetectors: {
    en: '/products/smoke-detectors',
    fi: '/tuotteet/palovaroittimet',
    sv: '/produkter/brandvarnare',
    da: '/produkter/røgalarmer',
    de: '/produkte/rauchmelder',
    pl: '/products/smoke-detectors',
  },
  centralUnit: {
    en: '/products/central-unit',
    fi: '/tuotteet/keskusyksikkö',
    sv: '/produkter/centralenhet',
    da: '/produkter/centralenhed',
    de: '/produkte/zentraleinheit',
    pl: '/products/central-unit',
  },
  thermostats: {
    en: '/products/thermostats',
    fi: '/tuotteet/huoneistotermostaatti',
    sv: '/produkter/termostater',
    da: '/produkter/termostater',
    de: '/produkte/thermostate',
    pl: '/products/thermostats',
  },
  underfloorHeating: {
    en: '/products/underfloor-heating',
    fi: '/tuotteet/lattialämmityksen-ohjaus',
    sv: '/produkter/golvvärmestyrning',
    da: '/produkter/gulvvarmestyring',
    de: '/produkte/fußbodenheizungssteuerung',
    pl: '/products/underfloor-heating',
  },
  radiatorHeating: {
    en: '/products/radiator-heating',
    fi: '/tuotteet/patterilämmityksen-ohjaus',
    sv: '/produkter/radiatorstyrning',
    da: '/produkter/radiatorstyring',
    de: '/produkte/heizkörpersteuerung',
    pl: '/products/radiator-heating',
  },

  // ─── Condition sensors ───────────────────────────────────────────────
  conditionSensors: {
    en: '/products/condition-sensors',
    fi: '/tuotteet/olosuhdesensorit',
    sv: '/produkter/klimatgivare',
    da: '/produkter/klimasensorer',
    de: '/produkte/raumklimasensoren',
    pl: '/products/condition-sensors',
  },
  climaPriDisplay: {
    en: '/products/condition-sensors/climapri-display',
    fi: '/tuotteet/olosuhdesensorit/climapri-näyttö',
    sv: '/produkter/klimatgivare/climapri-display',
    da: '/produkter/klimasensorer/climapri-display',
    de: '/produkte/raumklimasensoren/climapri-display',
    pl: '/products/condition-sensors/climapri-display',
  },
  apartmentSensor: {
    en: '/products/condition-sensors/apartment-sensor',
    fi: '/tuotteet/olosuhdesensorit/huoneistosensori',
    sv: '/produkter/klimatgivare/lägenhetsgivare',
    da: '/produkter/klimasensorer/lejlighedssensor',
    de: '/produkte/raumklimasensoren/wohnungssensor',
    pl: '/products/condition-sensors/apartment-sensor',
  },
  outdoorSensor: {
    en: '/products/condition-sensors/outdoor-sensor',
    fi: '/tuotteet/olosuhdesensorit/ulkoanturi',
    sv: '/produkter/klimatgivare/utomhusgivare',
    da: '/produkter/klimasensorer/udendørssensor',
    de: '/produkte/raumklimasensoren/außensensor',
    pl: '/products/condition-sensors/outdoor-sensor',
  },

  // ─── Water meters ────────────────────────────────────────────────────
  // Note: parent uses "etäluettava-vesimittari" (singular) per user's
  // confirmed slug list, while children sit under "vesimittarit" (plural).
  waterMeters: {
    en: '/products/water-meters',
    fi: '/tuotteet/etäluettava-vesimittari',
    sv: '/produkter/fjärravlasningsbara-vattenmätare',
    da: '/produkter/fjernaflæste-vandmålere',
    de: '/produkte/fernablesbare-wasserzähler',
    pl: '/products/water-meters',
  },
  // The wireless remote-read ultrasonic meter (Ultrimis NEO). The slug
  // carries the words people actually search for rather than the model name,
  // which also survives a model change.
  waterMetersUltrimisNeo: {
    en: '/products/water-meters/wireless-remote-read-water-meter',
    fi: '/tuotteet/vesimittarit/etäluettava-langaton-vesimittari',
    sv: '/produkter/vattenmätare/tradlos-fjarravlast-vattenmatare',
    da: '/produkter/vandmålere/traadloes-fjernaflaest-vandmaaler',
    de: '/produkte/wasserzähler/drahtloser-fernablesbarer-wasserzaehler',
    pl: '/products/water-meters/wireless-remote-read-water-meter',
  },
  waterMetersUltrasonicWireless: {
    en: '/products/water-meters/ultrasonic-wireless',
    fi: '/tuotteet/vesimittarit/ultraääni-lorawan',
    sv: '/produkter/vattenmätare/ultraljud-lorawan',
    da: '/produkter/vandmålere/ultralyd-lorawan',
    de: '/produkte/wasserzähler/ultraschall-lorawan',
    pl: '/products/water-meters/ultrasonic-wireless',
  },
  waterMetersUltrasonicMbus: {
    en: '/products/water-meters/ultrasonic-mbus',
    fi: '/tuotteet/vesimittarit/ultraääni-mbus',
    sv: '/produkter/vattenmätare/ultraljud-mbus',
    da: '/produkter/vandmålere/ultralyd-mbus',
    de: '/produkte/wasserzähler/ultraschall-mbus',
    pl: '/products/water-meters/ultrasonic-mbus',
  },
  waterMetersMechanical: {
    en: '/products/water-meters/mechanical',
    fi: '/tuotteet/vesimittarit/mekaaninen',
    sv: '/produkter/vattenmätare/mekanisk',
    da: '/produkter/vandmålere/mekanisk',
    de: '/produkte/wasserzähler/mechanisch',
    pl: '/products/water-meters/mechanical',
  },

  // ─── Energy meters ───────────────────────────────────────────────────
  energyMeters: {
    en: '/products/energy-meters',
    fi: '/tuotteet/energiamittarit',
    sv: '/produkter/energimätare',
    da: '/produkter/energimålere',
    de: '/produkte/energiezähler',
    pl: '/products/energy-meters',
  },
  sensoStarU: {
    en: '/products/energy-meters/sensostar-u',
    fi: '/tuotteet/energiamittarit/sensostar-u',
    sv: '/produkter/energimätare/sensostar-u',
    da: '/produkter/energimålere/sensostar-u',
    de: '/produkte/energiezähler/sensostar-u',
    pl: '/products/energy-meters/sensostar-u',
  },
  sensoStarC: {
    en: '/products/energy-meters/sensostar-c',
    fi: '/tuotteet/energiamittarit/sensostar-c',
    sv: '/produkter/energimätare/sensostar-c',
    da: '/produkter/energimålere/sensostar-c',
    de: '/produkte/energiezähler/sensostar-c',
    pl: '/products/energy-meters/sensostar-c',
  },
  flowStarU: {
    en: '/products/energy-meters/flowstar-u',
    fi: '/tuotteet/energiamittarit/flowstar-u',
    sv: '/produkter/energimätare/flowstar-u',
    da: '/produkter/energimålere/flowstar-u',
    de: '/produkte/energiezähler/flowstar-u',
    pl: '/products/energy-meters/flowstar-u',
  },

  // ─── ClimaPri Cloud (brand name — kept identical across languages) ──────────
  // Renamed from ClimaPri Cloud in September 2026. The old /mymetec paths are 301s
  // in netlify.toml; the route key keeps its old name so the rename did not
  // have to touch every call site.
  myMetec: {
    en: '/climapri-cloud',
    fi: '/climapri-cloud',
    sv: '/climapri-cloud',
    da: '/climapri-cloud',
    de: '/climapri-cloud',
    pl: '/climapri-cloud',
  },

  // ─── Partners ────────────────────────────────────────────────────────
  partnerPropertyManager: {
    en: '/partner/property-manager',
    fi: '/kumppanit/isännöitsijä',
    sv: '/partner/fastighetsförvaltare',
    da: '/partner/ejendomsadministrator',
    de: '/partner/hausverwaltung',
    pl: '/partner/property-manager',
  },
  partnerDesigner: {
    en: '/partner/designer',
    fi: '/kumppanit/suunnittelija',
    sv: '/partner/designer',
    da: '/partner/rådgivende-ingeniør',
    de: '/partner/planer',
    pl: '/partner/designer',
  },
  partnerContractor: {
    en: '/partner/contractor',
    fi: '/kumppanit/urakoitsija',
    sv: '/partner/entreprenör',
    da: '/partner/entreprenør',
    de: '/partner/auftragnehmer',
    pl: '/partner/contractor',
  },

  // ─── Resources / Blog ────────────────────────────────────────────────
  designMaterials: {
    en: '/resources/design-materials',
    fi: '/materiaalit/suunnittelumateriaalit',
    sv: '/resurser/designmaterial',
    da: '/ressourcer/projekteringsmateriale',
    de: '/ressourcen/planungsmaterial',
    pl: '/resources/design-materials',
  },
  blog: {
    en: '/blog',
    fi: '/blogi',
    sv: '/blogg',
    da: '/blog',
    de: '/blog',
    pl: '/blog',
  },
  smartBuildingGuide: {
    en: '/blog/smart-building-guide',
    fi: '/blogi/älykiinteistö-opas',
    sv: '/blogg/smart-byggnad-guide',
    da: '/blog/smart-bygning-guide',
    de: '/blog/smart-building-leitfaden',
    pl: '/blog/smart-building-guide',
  },
  energyEfficiencyDirective: {
    en: '/blog/energy-efficiency-directive',
    fi: '/blogi/energiatehokkuusdirektiivi',
    sv: '/blogg/energieffektivitetsdirektivet',
    da: '/blog/energieffektivitetsdirektivet',
    de: '/blog/energieeffizienzrichtlinie',
    pl: '/blog/energy-efficiency-directive',
  },
  avaraWaterCase: {
    en: '/blog/avara-water-savings-case',
    fi: '/blogi/avara-vesilaskutus',
    sv: '/blogg/avara-vattenbesparing',
    da: '/blog/avara-vandbesparelse',
    de: '/blog/avara-wassereinsparung',
    pl: '/blog/avara-water-savings-case',
  },
  ikaalinenHeatingCase: {
    en: '/blog/ikaalinen-district-heating-case',
    fi: '/blogi/ikaalinen-lammonohjaus',
    sv: '/blogg/ikaalinen-varmestyrning',
    da: '/blog/ikaalinen-varmestyring',
    de: '/blog/ikaalinen-heizungssteuerung',
    pl: '/blog/ikaalinen-district-heating-case',
  },
  swedenExpansion: {
    en: '/blog/climapri-sweden-expansion',
    fi: '/blogi/climapri-laajenee-ruotsiin',
    sv: '/blogg/climapri-expanderar-till-sverige',
    da: '/blog/climapri-ekspanderer-til-sverige',
    de: '/blog/climapri-expandiert-nach-schweden',
    pl: '/blog/climapri-sweden-expansion',
  },

  // Finnish-only articles (FI_ONLY_ROUTES). The other languages carry
  // placeholder slugs that are never generated and are 301'd to their blog.
  fiMandatoryWaterMeter: {
    en: '/blog/remote-read-water-meter-requirement',
    fi: '/blogi/etaluettava-vesimittari-pakollinen',
    sv: '/blogg/fjarravlast-vattenmatare-krav',
    da: '/blog/fjernaflaest-vandmaaler-krav',
    de: '/blog/fernablesbarer-wasserzaehler-pflicht',
    pl: '/blog/remote-read-water-meter-requirement',
  },
  fiMeteringSavings: {
    en: '/blog/apartment-water-metering-savings',
    fi: '/blogi/huoneistokohtainen-vedenmittaus-saasto',
    sv: '/blogg/lagenhetsvis-vattenmatning-besparing',
    da: '/blog/lejlighedsvis-vandmaaling-besparelse',
    de: '/blog/wohnungsweise-wassermessung-einsparung',
    pl: '/blog/apartment-water-metering-savings',
  },
  fiMeterTechComparison: {
    en: '/blog/remote-read-water-meter-mbus-wmbus-lorawan',
    fi: '/blogi/etaluettava-vesimittari-mbus-wmbus-lorawan',
    sv: '/blogg/fjarravlast-vattenmatare-mbus-wmbus-lorawan',
    da: '/blog/fjernaflaest-vandmaaler-mbus-wmbus-lorawan',
    de: '/blog/fernablesbarer-wasserzaehler-mbus-wmbus-lorawan',
    pl: '/blog/remote-read-water-meter-mbus-wmbus-lorawan',
  },
  fiLeakDetection: {
    en: '/blog/water-leak-detection',
    fi: '/blogi/vesivuodon-havaitseminen',
    sv: '/blogg/upptacka-vattenlackor',
    da: '/blog/opdage-vandlaekager',
    de: '/blog/wasserleck-erkennung',
    pl: '/blog/water-leak-detection',
  },
  // Finnish-only guides, scheduled weekly (SCHEDULED_ROUTES). The other
  // languages' slugs are never generated; they only satisfy the map's shape.
  fiDistrictHeatSavings: {
    en: '/blog/district-heating-savings',
    fi: '/blogi/kaukolammon-saasto-taloyhtiossa',
    sv: '/blogg/fjarrvarme-besparing',
    da: '/blog/fjernvarme-besparelse',
    de: '/blog/fernwaerme-einsparung',
    pl: '/blog/district-heating-savings',
  },
  fiSmokeAlarmDuty: {
    en: '/blog/smoke-alarm-responsibility',
    fi: '/blogi/palovaroitin-taloyhtio-vastuu',
    sv: '/blogg/brandvarnare-ansvar',
    da: '/blog/roegalarm-ansvar',
    de: '/blog/rauchmelder-verantwortung',
    pl: '/blog/smoke-alarm-responsibility',
  },
  housingCompanies: {
    en: '/housing-companies',
    fi: '/taloyhtiot',
    sv: '/bostadsrattsforeningar',
    da: '/boligforeninger',
    de: '/wohnungseigentuemer',
    pl: '/housing-companies',
  },
  fiEnergyGrant: {
    en: '/blog/energy-grant-housing-company',
    fi: '/blogi/energia-avustus-taloyhtio-2026',
    sv: '/blogg/energibidrag',
    da: '/blog/energitilskud',
    de: '/blog/energiefoerderung',
    pl: '/blog/energy-grant-housing-company',
  },
  fiWaterBilling: {
    en: '/blog/water-billing-housing-company',
    fi: '/blogi/vesilaskutus-taloyhtiossa',
    sv: '/blogg/vattendebitering',
    da: '/blog/vandafregning',
    de: '/blog/wasserabrechnung',
    pl: '/blog/water-billing-housing-company',
  },

  // ─── Company ─────────────────────────────────────────────────────────
  // ─── Solutions (Swedish market only) ─────────────────────────────────
  // These three pages exist only in Swedish — see SV_ONLY_ROUTES below.
  // LangPaths requires all five languages, so the other locales carry
  // placeholder slugs that are never generated and are 301'd in netlify.toml.
  // ─── Campaign landing (Finnish market only) ──────────────────────────
  // Printed on trade fair material as a QR code. The slug is permanent:
  // changing it breaks every printed code that is already in the wild.
  savingsEstimate: {
    en: '/savings-estimate',
    fi: '/saastoarvio',
    sv: '/besparingsanalys',
    da: '/besparelsesanalyse',
    de: '/einsparungsschaetzung',
    pl: '/savings-estimate',
  },

  solutionsOverview: {
    en: '/solutions',
    fi: '/ratkaisut',
    sv: '/losningar',
    da: '/losninger',
    de: '/loesungen',
    pl: '/solutions',
  },
  solutionsEnergyControl: {
    en: '/solutions/active-energy-control',
    fi: '/ratkaisut/energianohjaus',
    sv: '/losningar/aktiv-energistyrning',
    da: '/losninger/aktiv-energistyring',
    de: '/loesungen/aktive-energiesteuerung',
    pl: '/solutions/active-energy-control',
  },
  solutionsRoomComfort: {
    en: '/solutions/room-comfort',
    fi: '/ratkaisut/huonekohtainen-mukavuus',
    sv: '/losningar/rumsvis-varmestyrning',
    da: '/losninger/rumvis-varmestyring',
    de: '/loesungen/raumkomfort',
    pl: '/solutions/room-comfort',
  },
  solutionsMetering: {
    en: '/solutions/individual-metering',
    fi: '/ratkaisut/mittaus-ja-laskutus',
    sv: '/losningar/individuell-matning',
    da: '/losninger/individuel-maaling',
    de: '/loesungen/individuelle-messung',
    pl: '/solutions/individual-metering',
  },

  aboutUs: {
    en: '/about-us',
    fi: '/yritys',
    sv: '/om-oss',
    da: '/om-os',
    de: '/ueber-uns',
    pl: '/about-us',
  },
  careers: {
    en: '/careers',
    fi: '/tyopaikat',
    sv: '/karriar',
    da: '/karriere',
    de: '/karriere',
    pl: '/careers',
  },
  contact: {
    en: '/contact',
    fi: '/yhteystiedot',
    sv: '/kontakt',
    da: '/kontakt',
    de: '/kontakt',
    pl: '/contact',
  },

  // ─── Legal ───────────────────────────────────────────────────────────
  privacyPolicy: {
    en: '/privacy-policy',
    fi: '/tietosuoja',
    sv: '/integritetspolicy',
    da: '/privatlivspolitik',
    de: '/datenschutz',
    pl: '/privacy-policy',
  },
  termsOfService: {
    en: '/terms-of-service',
    fi: '/kayttoehdot',
    sv: '/anvandarvillkor',
    da: '/handelsbetingelser',
    de: '/nutzungsbedingungen',
    pl: '/terms-of-service',
  },
  // Legal notice / Julkaisutiedot / Impressum — published in fi, en and de only
  // (see marketLangsFor); the other slugs are never generated.
  legalNotice: {
    en: '/legal-notice',
    fi: '/julkaisutiedot',
    sv: '/legal-notice',
    da: '/legal-notice',
    de: '/impressum',
    pl: '/legal-notice',
  },
  cookiePolicy: {
    en: '/cookie-policy',
    fi: '/evastekaytanto',
    sv: '/cookies',
    da: '/cookies',
    de: '/cookie-richtlinie',
    pl: '/cookie-policy',
  },
};

/**
 * Routes that exist in the Nordic market microsites only (Swedish + Danish).
 * The Sweden market pages were built for that market alone and have no
 * fi/en/de content; Denmark mirrors them with its own hardcoded copy. So:
 *  - generateStaticParams skips them for every other language;
 *  - generateMetadata emits no cross-language hreflang alternates for them
 *    (pointing hreflang at pages that were never generated would be worse
 *    than omitting it);
 *  - netlify.toml 301s the placeholder slugs to each language's home page.
 * Same mechanism the `careers` route already uses for EN/SV.
 */
export const SOLUTIONS_ROUTES: readonly RouteName[] = [
  'solutionsOverview',
  'solutionsEnergyControl',
  'solutionsRoomComfort',
  'solutionsMetering',
];

/**
 * Routes that exist in Finnish only. Same mechanism as SOLUTIONS_ROUTES.
 * /fi/saastoarvio is a campaign landing page whose URL is printed on trade
 * fair material, so the slug must never change once published.
 */
export const FI_ONLY_ROUTES: readonly RouteName[] = ['savingsEstimate', 'fiMandatoryWaterMeter', 'fiMeteringSavings', 'fiMeterTechComparison', 'fiLeakDetection', 'fiDistrictHeatSavings', 'fiSmokeAlarmDuty', 'fiWaterBilling', 'fiEnergyGrant', 'housingCompanies'];

/**
 * Routes kept in the map but no longer published: not generated, not in
 * sitemap.xml, no hreflang. Their old URLs are 301'd in netlify.toml so any
 * external link or indexed result lands on the replacement.
 *
 * They stay here (and their page components stay in the repo) so a route can
 * be brought back by deleting one line rather than rebuilding it.
 *
 * waterMetersUltrasonicWireless: replaced by waterMetersUltrimisNeo, which
 * covers the same product with a keyword-led URL. Leaving both live had them
 * competing for the same query with the same product photo.
 */
export const RETIRED_ROUTES: readonly RouteName[] = ['waterMetersUltrasonicWireless'];

/**
 * Written but not yet published. Treated like a retired route — not
 * generated, not in sitemap.xml, no hreflang — and every list that links to
 * articles (blog landing, guide boxes, "Lue myös") skips it via isLiveRoute().
 *
 * The Finnish water-meter guides go out one a week. To publish the next one:
 * delete it from this list and set its real date in FI_ARTICLE_DATES
 * (src/components/resources/fi-articles/articleData.ts). Once the deploy is
 * live, ping Bing: `npm run indexnow -- <url of the guide> https://climapri.fi/fi/blogi`.
 */
export const SCHEDULED_ROUTES: readonly RouteName[] = [
  'fiDistrictHeatSavings',
  'fiSmokeAlarmDuty',
  'fiWaterBilling',
];

/** True when the route is published in at least one language. */
export function isLiveRoute(name: RouteName): boolean {
  return marketLangsFor(name)?.length !== 0;
}

/**
 * The languages a route is published in, or null when it exists in every
 * language. Used to skip static generation for the other languages and to keep
 * hreflang limited to the versions that actually exist.
 *
 * The solutions set is published in Swedish AND Danish — the two Nordic market
 * microsites — so these routes do cross-link each other in hreflang, unlike
 * the single-market Finnish campaign landing.
 */
export function marketLangsFor(name: RouteName): readonly Lang[] | null {
  // Retired: published in no language at all.
  if (RETIRED_ROUTES.includes(name)) return [];
  // Scheduled: written, not yet published.
  if (SCHEDULED_ROUTES.includes(name)) return [];
  // Energy control and metering are published in Finnish and English too —
  // both have their own pages for those two. The overview and room comfort
  // pages exist in the Nordic markets only, so /fi/ratkaisut and /en/solutions
  // stay unpublished (301 in netlify.toml) and those two navs show the pages
  // without a parent.
  if (name === 'solutionsEnergyControl' || name === 'solutionsMetering') return ['sv', 'da', 'fi', 'en'];
  if (SOLUTIONS_ROUTES.includes(name)) return ['sv', 'da'];
  if (FI_ONLY_ROUTES.includes(name)) return ['fi'];
  // Careers is written in English and Swedish only; /fi/tyopaikat and
  // /de/karriere are never generated and are 301'd to their language home in
  // netlify.toml. Declaring that here rather than as a special case in
  // generateStaticParams keeps hreflang honest too — before this, /en/careers
  // advertised fi/de alternates that pointed straight at those redirects.
  if (name === 'careers') return ['en', 'sv'];
  // The smart building guide was only ever written in English; the fi/sv/da/de
  // URLs served the same English text under foreign hreflang, so they are 301'd
  // to the English article in netlify.toml.
  if (name === 'smartBuildingGuide') return ['en'];
  // Legal notice (Impressum) is written for the fi, en and de versions.
  if (name === 'legalNotice') return LEGAL_TEXTS_READY ? ['fi', 'en', 'de'] : [];
  return null;
}

/**
 * Build the URL path for a route in a specific language.
 * Falls back to English when the lang isn't supported.
 */
export function pathFor(name: RouteName, lang: string | undefined): string {
  const langKey: Lang = (SUPPORTED_LANGS as readonly string[]).includes(lang || '')
    ? (lang as Lang)
    : 'en';
  const slug = ROUTES[name]?.[langKey] ?? ROUTES[name]?.en ?? '/';
  return `/${langKey}${slug}`;
}

/**
 * Canonical host per language. Each market gets its own ccTLD so search
 * engines (google.fi, google.se, google.pl) rank the localised version
 * higher than the generic .com. German has no dedicated domain yet, so
 * it lives on climapri.com under the /de/ path.
 */
const CANONICAL_HOST: Record<Lang, string> = {
  en: 'https://climapri.com',
  fi: 'https://climapri.fi',
  sv: 'https://climapri.se',
  da: 'https://climapri.dk',
  de: 'https://climapri.com',
  pl: 'https://climapri.pl',
};

/**
 * Return the canonical host (scheme + domain, no trailing slash) for a
 * language. Use this when generating canonical/og/hreflang URLs so that
 * Finnish content is canonical on climapri.fi, Swedish on climapri.se etc.
 */
export function canonicalHostFor(lang: string | undefined): string {
  const langKey: Lang = (SUPPORTED_LANGS as readonly string[]).includes(lang || '')
    ? (lang as Lang)
    : 'en';
  return CANONICAL_HOST[langKey];
}

/**
 * Open Graph locale per language. og:locale uses the underscore form
 * (language_TERRITORY), and the territory is NOT derivable from the language
 * code — sv is spoken in SE, da in DK, en defaults to US here. Deriving it
 * (`${lang}_${lang.toUpperCase()}`) yields sv_SV and da_DA, which are not
 * valid locales; both server and client metadata must read this map instead.
 */
const OG_LOCALE: Record<Lang, string> = {
  en: 'en_US',
  fi: 'fi_FI',
  sv: 'sv_SE',
  da: 'da_DK',
  de: 'de_DE',
  pl: 'pl_PL',
};

/** og:locale for a language, falling back to en_US for anything unknown. */
export function ogLocaleFor(lang: string | undefined): string {
  const langKey: Lang = (SUPPORTED_LANGS as readonly string[]).includes(lang || '')
    ? (lang as Lang)
    : 'en';
  return OG_LOCALE[langKey];
}

/**
 * Full canonical URL for a (route, language) pair, including host. Use
 * this in sitemap generation, SEO canonical tags and hreflang alternates.
 */
export function canonicalUrlFor(name: RouteName, lang: string | undefined): string {
  return canonicalHostFor(lang) + pathFor(name, lang);
}

/**
 * Reverse lookup: given a pathname like "/fi/tuotteet/palovaroittimet",
 * find the matching RouteName + lang. Used by the language switcher to
 * translate a slug from one language to another.
 *
 * Returns null when the path is the bare "/{lang}" landing or no slug
 * matches — the caller should fall back to swapping just the prefix.
 */
export function findRouteName(pathname: string): { name: RouteName; lang: Lang } | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const lang = parts[0];
  if (!(SUPPORTED_LANGS as readonly string[]).includes(lang)) return null;
  const slug = '/' + parts.slice(1).join('/');
  for (const key of Object.keys(ROUTES) as RouteName[]) {
    if (ROUTES[key][lang as Lang] === slug) {
      return { name: key, lang: lang as Lang };
    }
  }
  return null;
}

/**
 * Read the URL's language prefix from a pathname. Falls back to 'en'
 * when no recognized language prefix is present.
 *
 * This is used in App.tsx instead of useParams() because the generated
 * routes use literal language prefixes (e.g. `/fi/tuotteet/...`) rather
 * than a `:lang` param, so React Router's useParams returns nothing.
 */
export function langFromPathname(pathname: string): Lang {
  const first = pathname.split('/').filter(Boolean)[0];
  return ((SUPPORTED_LANGS as readonly string[]).includes(first) ? first : 'en') as Lang;
}

/**
 * Reverse lookup by slug: given a slug like "/products/smoke-detectors"
 * (no language prefix) and the language it's currently in, return the
 * route name. Returns null when no match — the language switcher uses
 * that as a signal to fall back to the homepage in the target language.
 */
export function routeNameFromSlug(slug: string, lang: Lang): RouteName | null {
  // Normalise to NFC so that ä/ö/ß slugs match regardless of the Unicode
  // normalisation form (NFC vs NFD) the caller/router hands us — otherwise
  // e.g. /tuotteet/keskusyksikkö fails to resolve and the page renders empty.
  const s = slug.normalize('NFC');
  for (const key of Object.keys(ROUTES) as RouteName[]) {
    if (ROUTES[key][lang].normalize('NFC') === s) return key;
  }
  return null;
}

/**
 * Legacy English slugs from the previous URL scheme — kept so that the app
 * (and Netlify 301 rules) can redirect old URLs to the new canonical ones.
 */
export const LEGACY_REDIRECTS: Record<string, RouteName> = {
  '/products/climapri/district-heating-control': 'centralUnit',
  '/products/climapri-temperature-control': 'thermostats',
  '/products/climapri/underfloor-heating-control': 'underfloorHeating',
  '/products/climapri/radiator-heating-control': 'radiatorHeating',
  '/products/water-meters/mechanical-mbus': 'waterMetersMechanical',
};
